import type { PayloadAction } from "@reduxjs/toolkit";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { IThemeInfo } from "../../../model/model";
import axios from "axios";
import apiUrls from "../../../api";
import type { IAppLoaderAction } from "../../../common/state/loaderHandleMiddleware";
import type { RootState } from "../../../common/state/store";
import { omitStrapiEntityMetaKeys, resolveDocumentId } from "../../../common/util/strapiDocumentId";

interface IThemes {
  all: IThemeInfo[];
}

export const fetchThemes = createAsyncThunk(
  "themes/fetchThemes",
  // eslint-disable-next-line no-empty-pattern
  async ({}: IAppLoaderAction) => {
    return await axios
      .get<IThemeInfo[]>(`${apiUrls.themeInfos}?populate=organization&select=organization.id`)
      .then((res) => {
        return res.data;
      })
      .catch((error: unknown) => {
        console.error(error);
        return undefined;
      });
  },
);

export const updateTheme = createAsyncThunk(
  "themes/updateTheme",

  async ({ theme }: { theme: IThemeInfo } & IAppLoaderAction) => {
    const documentId = await resolveDocumentId(apiUrls.themeInfos, theme.id, theme);
    return await axios
      .put<IThemeInfo>(
        `${apiUrls.themeInfos}/${documentId}`,
        omitStrapiEntityMetaKeys({ ...theme }),
      )
      .then((res) => {
        return res.data;
      })
      .catch((error: unknown) => {
        console.error(error);
        return undefined;
      });
  },
);

export const postNewTheme = createAsyncThunk(
  "themes/postNewTheme",

  async ({ theme }: { theme: IThemeInfo } & IAppLoaderAction) => {
    return await axios
      .post<IThemeInfo>(
        `${apiUrls.themeInfos}`,
        omitStrapiEntityMetaKeys({ ...theme, organization: theme.organization?.id }),
      )
      .then((res) => {
        return res.data;
      })
      .catch((error: unknown) => {
        console.error(error);
        return undefined;
      });
  },
);

const initialState: IThemes = {
  all: [],
};

export const themesSlice = createSlice({
  name: "themes",
  initialState,
  reducers: {
    deleteThemeInStore: (state: IThemes, { payload }: PayloadAction<{ themeId: number }>) => {
      state.all = state.all.filter((theme) => theme.id !== payload.themeId);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchThemes.fulfilled, (state: IThemes, { payload }) => {
      if (payload) {
        state.all = payload;
      }
    });
  },
});

export const themesReducer = themesSlice.reducer;

export const getThemes = (state: RootState) => state.themes.all;

export const { deleteThemeInStore } = themesSlice.actions;
